import { Link } from "react-router-dom";
import {
  Sparkles,
  Music,
  Activity,
  Candy,
  Footprints,
  Lightbulb,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";

const techniques = [
  {
    id: "distraction",
    icon: Activity,
    label: "Technique 01",
    title: "Distract the body and the mind, together.",
    blurb:
      "When a seizure begins, engaging in something that occupies both your mind and your movement at once can interrupt it. Activities like rock climbing, Just Dance, or anything that demands physical and mental focus appear to help reset the nervous system — and often stop the seizure in its tracks.",
    examples: ["Rock climbing", "Just Dance", "Catching/throwing a ball", "Stair climbing while counting"],
    testid: "treatment-distraction",
  },
  {
    id: "shock-the-senses",
    icon: Candy,
    label: "Technique 02",
    title: "A sudden, strong sensory input.",
    blurb:
      "An intense taste — extremely sour or strikingly sweet — can act as a circuit-breaker. The body's response to the shock can be enough to halt a seizure or pull you back from the edge of one.",
    examples: [
      "Warhead-style sour sweets",
      "A spoon of sugar",
      "A slice of lemon",
      "Strong mints",
    ],
    testid: "treatment-shock",
  },
  {
    id: "automatic-movement",
    icon: Footprints,
    label: "Technique 03",
    title: "Trigger an automatic movement.",
    blurb:
      "If your legs are caught in functional paralysis, performing a familiar, semi-automatic action — like putting socks on and taking them off — can sometimes coax movement back. The brain falls back on a well-rehearsed pattern instead of the one that's stuck.",
    examples: ["Putting socks on and off", "Tying shoelaces", "Pedalling motion while seated"],
    testid: "treatment-automatic",
  },
  {
    id: "singing",
    icon: Music,
    label: "Technique 04",
    title: "Sing your way back into speech.",
    blurb:
      "When speech becomes slurred or disappears entirely, singing or rapping along to a familiar song often returns it. Music engages a different neural pathway than ordinary speech, and can quietly coax the words back out.",
    examples: ["Singing a favourite song", "Rapping along to lyrics you know", "Humming a tune you love"],
    testid: "treatment-singing",
  },
];

export default function Treatments() {
  return (
    <div data-testid="page-treatments">
      {/* Header */}
      <section className="pt-16 lg:pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-8">
            <span className="w-6 h-px bg-terracotta" />
            Chapter Three
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <h1
              data-testid="treatments-title"
              className="lg:col-span-8 font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl lg:text-7xl leading-[1.03] tracking-tight text-balance"
            >
              Treatments &
              <span className="italic text-terracotta"> techniques </span>
              that helped me.
            </h1>
            <p className="lg:col-span-4 text-lg text-charcoal-light dark:text-bone/70 leading-relaxed">
              A small set of practical strategies, taught to me by my FND
              specialist. They aren’t cures — they’re circuit-breakers,
              shared in case any of them quietly help you too.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="pb-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div
            data-testid="treatments-disclaimer"
            className="flex flex-col md:flex-row gap-5 md:items-center rounded-3xl border border-moss/30 bg-moss/10 dark:bg-moss/15 p-6 lg:p-8"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-moss/20 text-moss shrink-0">
              <ShieldCheck size={20} strokeWidth={1.5} />
            </span>
            <p className="text-base text-charcoal dark:text-bone leading-relaxed">
              <strong className="font-semibold">Patient experience, not medical advice.</strong>{" "}
              These techniques came from my own specialist and have helped me
              personally. Please discuss any new strategy with a clinician
              who knows your case before relying on it during a seizure or flare.
            </p>
          </div>
        </div>
      </section>

      {/* Why this works — context */}
      <section className="pb-16" data-testid="treatments-why-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <span className="md:col-span-3 text-xs uppercase tracking-[0.28em] text-moss">
              The shared idea
            </span>
            <div className="md:col-span-9">
              <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl leading-[1.08] tracking-tight text-balance">
                Most of these techniques rely on one thing: <em className="text-terracotta">interruption</em>.
              </h2>
              <p className="mt-5 text-lg text-charcoal-light dark:text-bone/70 leading-relaxed">
                FND symptoms tend to be driven by repeating misfires in the
                brain. Distracting that pattern — with movement, sound, taste,
                or a familiar action — can break the loop and let the body
                find its way back to baseline.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Techniques list */}
      <section className="pb-20 lg:pb-28" data-testid="treatments-list">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 space-y-6">
          {techniques.map((t) => {
            const Icon = t.icon;
            return (
              <article
                key={t.id}
                data-testid={t.testid}
                className="group rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 p-8 lg:p-10 hover-lift transition-colors hover:border-terracotta/40"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
                  <div className="md:col-span-3 flex md:flex-col items-center md:items-start gap-4">
                    <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-terracotta/10 text-terracotta transition-all duration-500 group-hover:bg-terracotta group-hover:text-bone">
                      <Icon size={22} strokeWidth={1.5} />
                    </span>
                    <span className="text-xs uppercase tracking-[0.28em] text-charcoal-light dark:text-bone/55">
                      {t.label}
                    </span>
                  </div>
                  <div className="md:col-span-9 space-y-5">
                    <h3 className="font-serif text-2xl sm:text-3xl text-charcoal dark:text-bone tracking-tight leading-snug text-balance">
                      {t.title}
                    </h3>
                    <p className="text-base lg:text-lg text-charcoal-light dark:text-bone/75 leading-relaxed">
                      {t.blurb}
                    </p>
                    <div className="pt-2 border-t border-bone-200 dark:border-charcoal-light/25">
                      <p className="text-[0.65rem] uppercase tracking-[0.28em] text-charcoal-light dark:text-bone/55 mt-3 mb-3">
                        Examples
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {t.examples.map((ex) => (
                          <span
                            key={ex}
                            className="inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-medium bg-terracotta/10 text-terracotta dark:bg-terracotta/15"
                          >
                            {ex}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Insight callout */}
      <section className="py-20 lg:py-28 bg-bone-100/60 dark:bg-charcoal-light/10" data-testid="treatments-insight-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
            <span className="w-6 h-px bg-terracotta" />
            Why distraction works
          </span>
          <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
            The brain is misfiring. Distraction is a way of <em className="text-terracotta">changing the channel</em>.
          </h2>
          <div className="mt-8 space-y-5 text-lg text-charcoal-light dark:text-bone/75 leading-relaxed">
            <p>
              FND symptoms are caused by the brain sending the wrong signals
              — a pattern that can self-reinforce once it begins. Almost
              every technique above works by introducing something the brain
              can’t ignore: a strong taste, a song, a familiar movement, an
              activity that demands attention.
            </p>
            <p>
              Each one nudges the brain off its loop and gives it a chance
              to start again. It’s not magic, and it doesn’t always work.
              But when it does, it’s quiet and remarkable.
            </p>
          </div>

          <div className="mt-12 flex items-start gap-4 rounded-3xl border border-terracotta/30 bg-terracotta/10 dark:bg-terracotta/15 p-6 lg:p-8">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-terracotta/20 text-terracotta shrink-0">
              <Lightbulb size={20} strokeWidth={1.5} />
            </span>
            <p className="text-base text-charcoal dark:text-bone leading-relaxed">
              <strong className="font-semibold">A note on building your own toolkit.</strong>{" "}
              The techniques that work best are usually the ones already
              woven into your life — your music, your hobbies, your
              comfort objects. Try a few. Keep what helps. Discard what doesn’t.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link
            to="/resources"
            data-testid="treatments-cta-resources"
            className="group relative block overflow-hidden rounded-[2rem] bg-charcoal dark:bg-bone-100/10 text-bone p-10 lg:p-14 hover-lift border border-transparent dark:border-bone-200/10"
          >
            <span className="text-xs uppercase tracking-[0.28em] text-bone/60">If you need more</span>
            <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Helplines, organisations & quiet support.
            </h3>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium border-b border-bone/40 pb-1 group-hover:border-terracotta group-hover:text-terracotta transition">
              Open resources
              <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
            <div className="pointer-events-none absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-terracotta/20 blur-3xl" />
          </Link>
        </div>
      </section>

      {/* Closing */}
      <section className="pb-24 lg:pb-32" data-testid="treatments-closing">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <p className="font-serif italic text-2xl sm:text-3xl text-charcoal dark:text-bone leading-snug text-balance">
            “What helps is often quieter — and stranger — than you’d expect. Trust the small experiments.”
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.28em] text-charcoal-light dark:text-bone/60">
            A note from the author
          </p>
        </div>
      </section>
    </div>
  );
}
